import React from 'react';
import { Switch, Button, Tag } from 'antd';
import { FiPlay } from 'react-icons/fi';
import { AiOutlineDelete } from 'react-icons/ai';
import { StatusBadge } from '@components/status-badge';
import { AirflowDag, AirflowDagDetails, AirflowDagRun } from '../types';
import { DagRunStateBadge, formatDagVersion, formatDateTimeUtc, formatSchedule } from '../utils';
import styles from '../styles.module.scss';

type DagHeaderProps = {
    dagId: string;
    dag: AirflowDag | null;
    details: AirflowDagDetails | null;
    latestRun: AirflowDagRun | null;
    isLoadingDag: boolean;
    togglingPause: boolean;
    triggering: boolean;
    onTogglePause: () => void;
    onTrigger: () => void;
    onDelete: () => void;
};

const MAX_VISIBLE_TAGS = 4;

/*
 * DAG detail header: title, pause/trigger/delete actions, and a metadata grid
 */
export function DagHeader({ dagId, dag, details, latestRun, isLoadingDag, togglingPause, onTogglePause, onTrigger, onDelete }: DagHeaderProps) {
    if (isLoadingDag || !dag) {
        return (
            <div className={styles.headerRow}>
                <h1>{dagId}</h1>
            </div>
        );
    }

    const displayName = dag.dag_display_name ?? dag.dag_id;
    const tags = dag.tags ?? [];
    const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
    const overflowTags = tags.length - visibleTags.length;

    return (
        <div>
            <div className={styles.headerRow}>
                <h1>
                    {displayName}
                    {displayName !== dag.dag_id && <span style={{ fontSize: 13, color: '#666a8d', fontWeight: 400 }}>{dag.dag_id}</span>}
                    <Switch checked={!dag.is_paused} loading={togglingPause} onChange={onTogglePause} checkedChildren="Active" unCheckedChildren="Paused" />
                    {dag.has_import_errors && <StatusBadge variant="destructive">Import Error</StatusBadge>}
                </h1>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button icon={undefined} onClick={onTrigger} disabled={dag.is_paused}>
                        <FiPlay style={{ marginRight: 6 }} />
                        Trigger
                    </Button>
                    <Button type="danger" onClick={onDelete}>
                        <AiOutlineDelete style={{ marginRight: 6 }} />
                        Delete DAG
                    </Button>
                </div>
            </div>
            <div className={styles.metaGrid}>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Schedule</div>
                    <div className={styles.metaValue}>{formatSchedule(dag.timetable_description, dag.timetable_summary)}</div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Latest Run</div>
                    <div className={styles.metaValue}>
                        {latestRun ? (
                            <>
                                <DagRunStateBadge state={latestRun.state} /> {formatDateTimeUtc(latestRun.execution_date ?? latestRun.run_after ?? latestRun.logical_date)}
                            </>
                        ) : (
                            '—'
                        )}
                    </div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Next Run</div>
                    <div className={styles.metaValue}>{formatDateTimeUtc(dag.next_dagrun ?? dag.next_dagrun_logical_date ?? dag.next_dagrun_run_after)}</div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Max Active Runs</div>
                    <div className={styles.metaValue}>{dag.max_active_runs ?? '—'}</div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Owner</div>
                    <div className={styles.metaValue}>{dag.owners?.join(', ') || '—'}</div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Latest Version</div>
                    <div className={styles.metaValue}>{formatDagVersion(details?.latest_dag_version ? [details.latest_dag_version] : undefined)}</div>
                </div>
                <div className={styles.metaField}>
                    <div className={styles.metaLabel}>Tags</div>
                    <div className={styles.metaValue}>
                        {visibleTags.length === 0 && '—'}
                        {visibleTags.map((tag) => (
                            <Tag key={tag.name}>{tag.name}</Tag>
                        ))}
                        {overflowTags > 0 && <Tag>{`+${overflowTags}`}</Tag>}
                    </div>
                </div>
            </div>
        </div>
    );
}
